import React from 'react';
import { 
  TrendingUp, 
  Users,
  ExternalLink
} from 'lucide-react';
import { formatCompactCurrency, formatDate } from '@/utils';

interface ProjectFundingRound {
  round_name: string;
  date: string;
  fully_diluted_valuation: number;
  amount_raised: number;
  investors: string[];
}

interface FundingRoundDropdownProps {
  round: ProjectFundingRound;
  index: number;
}

const FundingRoundDropdown: React.FC<FundingRoundDropdownProps> = ({ round, index }) => {
  // Only show amount raised if it's greater than 0
  const showAmountRaised = (round.amount_raised || 0) > 0;
  // Only show investors if there are any
  const showInvestors = round.investors && round.investors.length > 0;

  return (
    <div className="bg-[rgba(255,255,255,0.44)] dark:bg-[rgba(28,28,28,0.44)] p-5">
      {/* Header Section */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          {/* Icon container */}
          <div className="w-12 h-12 bg-[rgba(200,237,3,0.16)] dark:bg-[rgba(200,237,3,0.12)] flex items-center justify-center flex-shrink-0">
            <TrendingUp size={24} className="text-[#9db807]" />
          </div>
          {/* Round info */}
          <div className="flex flex-col">
            <h3 className="font-['Plus_Jakarta_Sans'] font-medium text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
              {round.round_name || `Round ${index + 1}`}
            </h3>
            <p className="font-['Plus_Jakarta_Sans'] font-normal text-[13px] leading-[18px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">
              {formatDate(round.date)}
            </p>
          </div>
        </div>
        
        {/* Amount raised */}
        {showAmountRaised && (
          <div className="text-right">
            <p className="font-['Plus_Jakarta_Sans'] font-medium text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
              {formatCompactCurrency(round.amount_raised || 0)}
            </p>
          </div>
        )}
      </div>

      {/* Investors Section */}
      {showInvestors && round.investors && (
        <>
          {/* Divider */}
          <div className="border-t border-[rgba(45,41,35,0.08)] dark:border-[rgba(253,253,253,0.08)] mb-4" />
          
          {/* Total Investors header */}
          <div className="flex items-center gap-[6px] mb-3">
            <Users size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
              Total Investors
            </span>
          </div>
          
          {/* Investor badges */}
          <div className="flex flex-wrap gap-2">
            {round.investors.map((investor: string, investorIndex: number) => (
              <div
                key={investorIndex}
                className="flex items-center gap-[6px] bg-white dark:bg-[rgba(255,255,255,0.08)] border border-[rgba(45,41,35,0.08)] dark:border-[rgba(253,253,253,0.08)] px-3 py-[6px]"
              >
                <ExternalLink size={10} className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />
                <span className="font-['Plus_Jakarta_Sans'] font-normal text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                  {investor}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default FundingRoundDropdown;
